"use client";

import dynamic from "next/dynamic";
import React, { useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Box,
  Group,
  Pill,
  PillsInput,
  Text,
  UnstyledButton,
  Box as MantineBox,
} from "@mantine/core";
import { IconPhotoSearch, IconArrowRight } from "@tabler/icons-react";
import styles from "./styles.module.scss";

const Typewriter = dynamic(() => import("typewriter-effect"), { ssr: false });
const TYPEWRITER_DELAY_MS = 2500;

interface SearchInputProps {
  query: string;
  selected: SelectedFilter[];
  isFocused: boolean;
  compactMode: boolean;
  useTypewriter: boolean;
  brandStrings: string[];
  staticPlaceholder: string;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onQueryChange: (value: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onRemove: (label: string) => void;
  onImageSearchToggle: () => void;
  onNavigate: (path: string) => void;
  onNavigateWithHighlighted: () => boolean;
  onKeywordAdd: () => void;
  searchPath: string;
}

export const SearchInput = React.memo(
  React.forwardRef<HTMLDivElement, SearchInputProps>(
    (
      {
        query,
        selected,
        isFocused,
        compactMode,
        useTypewriter,
        brandStrings,
        staticPlaceholder,
        containerRef,
        onQueryChange,
        onFocus,
        onBlur,
        onKeyDown,
        onRemove,
        onImageSearchToggle,
        onNavigate,
        onNavigateWithHighlighted,
        onKeywordAdd,
        searchPath,
      },
      ref
    ) => {
      const inputRef = useRef<HTMLInputElement>(null);
      const [animatePlaceholder, setAnimatePlaceholder] = useState(false);

      useEffect(() => {
        const timer = window.setTimeout(() => setAnimatePlaceholder(true), TYPEWRITER_DELAY_MS);
        return () => window.clearTimeout(timer);
      }, []);

      const showTypewriter = useTypewriter && !query && !isFocused;

      return (
        <Box
          w="100%"
          maw={850}
          ref={(node: HTMLDivElement | null) => {
            // Forward both refs: Popover's positioning ref and the containerRef
            if (typeof ref === "function") ref(node);
            else if (ref) ref.current = node;
            if (containerRef)
              (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
          }}
        >
          <MantineBox
            className={`${styles.searchBox} ${styles.pillInput}`}
            data-testid="search-input"
          >
            <PillsInput
              wrapperProps={{ style: { width: "100%", height: "100%" } }}
              onClick={() => inputRef.current?.focus()}
              p={0}
              size="sm"
              className={styles.pillsInputField}
            >
              <Box
                w="100%"
                p="sm"
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Box style={{ position: "relative", width: "100%" }}>
                  {showTypewriter && (
                    <Box
                      className={`${styles.typewriterOverlay} ${compactMode ? styles.compact : ""}`}
                    >
                      <Group gap={0} align="center">
                        <Box component="span" hiddenFrom="sm" mr="4px">
                          Search for
                        </Box>
                        {animatePlaceholder ? (
                          <Typewriter
                            options={{
                              strings: brandStrings,
                              autoStart: true,
                              loop: true,
                              deleteSpeed: 50,
                              delay: 80,
                            }}
                          />
                        ) : (
                          <Text component="span">{brandStrings[0] || "Raf Simons"}</Text>
                        )}
                      </Group>
                    </Box>
                  )}

                  <PillsInput.Field
                    ref={inputRef}
                    data-testid="input-field"
                    aria-label="Search for brands, styles, and more"
                    autoComplete="off"
                    className={styles.pillInputField}
                    w="100%"
                    value={query}
                    onChange={(e) => onQueryChange(e.currentTarget.value)}
                    onKeyDown={onKeyDown}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    placeholder={staticPlaceholder}
                    style={{
                      width: "100%",
                      position: "relative",
                      zIndex: 10,
                      background: "transparent",
                      border: "none",
                      outline: "none",
                      fontSize: compactMode ? "14px" : "16px",
                    }}
                  />
                </Box>

                {selected.length > 0 && (
                  <Group gap="xs" mt="xs">
                    {selected.map((filter) => (
                      <Pill
                        key={`${filter.key}-${filter.value}`}
                        data-testid={`pill-${filter.label}`}
                        withRemoveButton
                        onRemove={() => onRemove(filter.label)}
                        size="sm"
                      >
                        {filter.label}
                      </Pill>
                    ))}
                  </Group>
                )}
              </Box>
            </PillsInput>

            <Group data-btncontainer gap={4} align="center" style={{ flexShrink: 0 }} px={12}>
              {query ? (
                <ActionIcon
                  variant="transparent"
                  onClick={() => {
                    if (!onNavigateWithHighlighted()) {
                      onKeywordAdd();
                    }
                  }}
                  className={styles.textSearchIcon}
                  aria-label="Submit search"
                  size={44}
                >
                  <IconArrowRight size={24} color="black" />
                </ActionIcon>
              ) : (
                <>
                  <ActionIcon
                    variant="transparent"
                    onClick={onImageSearchToggle}
                    aria-label="Search by image"
                    data-imagesearch
                    size={44}
                    className={styles.imageSearchIcon}
                  >
                    <IconPhotoSearch size={20} color="black" />
                  </ActionIcon>
                  <UnstyledButton
                    type="button"
                    className={styles.searchText}
                    onClick={() => onNavigate(searchPath)}
                    aria-label="Open search results"
                  >
                    SEARCH
                  </UnstyledButton>
                </>
              )}
            </Group>
          </MantineBox>
        </Box>
      );
    }
  )
);

SearchInput.displayName = "SearchInput";
